/**
 * Thumbnail Request Manager - Handles throttling and caching of thumbnail requests
 * to prevent NS_BINDING_ABORTED errors from overwhelming the camera service
 */

interface ThumbnailRequest {
  cameraId: number;
  timestamp: number;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: any) => void;
}

class ThumbnailRequestManager {
  private requestQueue: ThumbnailRequest[] = [];
  private activeRequests = new Set<number>();
  private maxConcurrentRequests = 2; // Increased back to 2 for faster loading
  private requestDelay = 500; // Reduced from 2000ms to 500ms for faster loading
  private lastRequestTime = 0;
  private cache = new Map<number, { url: string; timestamp: number }>();
  private cacheTimeout = 60000; // 60 seconds cache timeout (longer to reduce requests)

  /**
   * Request a thumbnail for a camera with throttling
   */
  async requestThumbnail(cameraId: number, thumbnailUrl: string): Promise<string> {
    // Check cache first
    const cached = this.cache.get(cameraId);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < this.cacheTimeout) {
      console.log(`Using cached thumbnail for camera ${cameraId}`);
      return cached.url;
    }

    // If already requesting this camera, return cached or wait briefly
    if (this.activeRequests.has(cameraId)) {
      console.log(`Camera ${cameraId} already being requested, using fallback`);
      // Return cached if available, otherwise fallback to direct URL
      if (cached) {
        return cached.url;
      }
      return `${thumbnailUrl}?t=${now}`;
    }

    // If queue is getting too long, bypass the manager
    if (this.requestQueue.length > 5) {
      console.warn(`Request queue too long (${this.requestQueue.length}), bypassing manager for camera ${cameraId}`);
      return `${thumbnailUrl}?t=${now}`;
    }

    // Add to queue if we're at max concurrent requests
    if (this.activeRequests.size >= this.maxConcurrentRequests) {
      return this.queueRequest(cameraId, thumbnailUrl);
    }

    // Execute request immediately
    return this.executeRequest(cameraId, thumbnailUrl);
  }

  private async waitForExistingRequest(cameraId: number, thumbnailUrl: string): Promise<string> {
    // Wait for the existing request to complete
    while (this.activeRequests.has(cameraId)) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Check cache again after waiting
    const cached = this.cache.get(cameraId);
    if (cached) {
      return cached.url;
    }
    
    // If still no cache, make a new request
    return this.executeRequest(cameraId, thumbnailUrl);
  }

  private async queueRequest(cameraId: number, thumbnailUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const request: ThumbnailRequest = {
        cameraId,
        timestamp: Date.now(),
        promise: Promise.resolve(),
        resolve: () => {
          this.executeRequest(cameraId, thumbnailUrl)
            .then(resolve)
            .catch(reject);
        },
        reject
      };
      
      this.requestQueue.push(request);
      this.processQueue();
    });
  }

  private async executeRequest(cameraId: number, thumbnailUrl: string): Promise<string> {
    this.activeRequests.add(cameraId);
    
    try {
      // Implement delay between requests to prevent overwhelming the server
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;
      if (timeSinceLastRequest < this.requestDelay) {
        await new Promise(resolve => setTimeout(resolve, this.requestDelay - timeSinceLastRequest));
      }
      this.lastRequestTime = Date.now();

      // Create a unique URL with timestamp to bypass browser cache
      const timestamp = Date.now();
      const urlWithTimestamp = `${thumbnailUrl}?t=${timestamp}`;
      
      // Pre-load the image to check if it's available
      await this.preloadImage(urlWithTimestamp);
      
      // Cache the successful URL
      this.cache.set(cameraId, {
        url: urlWithTimestamp,
        timestamp: Date.now()
      });
      
      return urlWithTimestamp;
      
    } catch (error) {
      console.warn(`Thumbnail request failed for camera ${cameraId}:`, error);
      
      // Return cached URL if available, otherwise return original URL
      const cached = this.cache.get(cameraId);
      if (cached) {
        return cached.url;
      }
      
      // Return original URL as fallback
      return `${thumbnailUrl}?t=${Date.now()}`;
      
    } finally {
      this.activeRequests.delete(cameraId);
      this.processQueue();
    }
  }

  private preloadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        img.src = ''; // Clear src to stop loading
        reject(new Error('Request timeout'));
      }, 25000); // 25 second timeout (longer than camera service timeout)
      
      img.onload = () => {
        clearTimeout(timeout);
        img.onload = null;
        img.onerror = null;
        resolve();
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        img.onload = null;
        img.onerror = null;
        reject(new Error('Image load failed'));
      };
      
      img.src = url;
    });
  }

  private processQueue() {
    // Process queued requests if we have capacity
    while (this.requestQueue.length > 0 && this.activeRequests.size < this.maxConcurrentRequests) {
      const request = this.requestQueue.shift();
      if (request) {
        // Check if request is still relevant (not too old)
        const age = Date.now() - request.timestamp;
        if (age < 15000) { // 15 seconds max age (shorter to prevent stale requests)
          request.resolve();
        } else {
          console.warn(`Dropping stale request for camera ${request.cameraId} (age: ${age}ms)`);
          request.reject(new Error('Request timeout'));
        }
      }
    }
  }

  /**
   * Clear cache for a specific camera
   */
  clearCache(cameraId?: number) {
    if (cameraId !== undefined) {
      this.cache.delete(cameraId);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get current status for debugging
   */
  getStatus() {
    return {
      activeRequests: Array.from(this.activeRequests),
      queueLength: this.requestQueue.length,
      cacheSize: this.cache.size,
      maxConcurrent: this.maxConcurrentRequests
    };
  }
}

// Export singleton instance
export const thumbnailRequestManager = new ThumbnailRequestManager();
